/**
 * SubcategoryPicker — 2-tier grouped interest selector.
 *
 * Props:
 *   categories  – flat array of ALL categories (parents + sub-categories)
 *   selected    – array of currently selected sub-category IDs
 *   onChange    – (newSelectedIds: string[]) => void
 *   testidPrefix – optional prefix for data-testid attributes
 */
import { useState, useMemo, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { InterestIcon } from './InterestIcon';

const SubcategoryPicker = ({ categories = [], selected = [], onChange, testidPrefix = 'subcat' }) => {
  const parents = useMemo(
    () => categories.filter((c) => !c.parent_id),
    [categories],
  );

  const subsByParent = useMemo(() => {
    const map = {};
    categories.filter((c) => c.parent_id).forEach((c) => {
      (map[c.parent_id] = map[c.parent_id] || []).push(c);
    });
    return map;
  }, [categories]);

  // All sections collapsed by default; auto-expand sections with selected sub-categories
  const [open, setOpen] = useState({});

  useEffect(() => {
    const toOpen = {};
    parents.forEach((p) => {
      const subs = subsByParent[p.id] || [];
      if (subs.some((s) => selected.includes(s.id))) toOpen[p.id] = true;
    });
    if (Object.keys(toOpen).length > 0) {
      setOpen((prev) => ({ ...prev, ...toOpen }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // mount only — intentionally snapshot the initial selection

  const toggle = (id) =>
    onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);

  const toggleSection = (parentId) =>
    setOpen((prev) => ({ ...prev, [parentId]: !prev[parentId] }));

  return (
    <div
      className="border border-gray-200 rounded-lg overflow-hidden divide-y divide-gray-100"
      data-testid={`${testidPrefix}-picker`}
    >
      {parents.map((parent) => {
        const subs = subsByParent[parent.id] || [];
        if (subs.length === 0) return null;
        const isOpen = !!open[parent.id];
        const selectedCount = subs.filter((s) => selected.includes(s.id)).length;

        return (
          <div key={parent.id}>
            <button
              type="button"
              onClick={() => toggleSection(parent.id)}
              className="w-full flex items-center justify-between px-3 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
              data-testid={`${testidPrefix}-parent-${parent.id}`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <InterestIcon icon={parent.icon} size={14} className="text-gray-500 flex-shrink-0" />
                <span className="text-sm font-medium text-gray-700 truncate">{parent.name}</span>
                {selectedCount > 0 && (
                  <span className="flex-shrink-0 px-1.5 py-0.5 bg-green-100 text-green-700 text-xs rounded-full font-medium leading-none">
                    {selectedCount}
                  </span>
                )}
              </div>
              <ChevronDown
                size={14}
                className={`text-gray-400 flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
              />
            </button>

            {isOpen && (
              <div className="flex flex-wrap gap-1.5 px-3 py-2.5 bg-white">
                {subs.map((sub) => {
                  const active = selected.includes(sub.id);
                  const icon = sub.icon || parent.icon; // fallback to parent icon
                  return (
                    <button
                      key={sub.id}
                      type="button"
                      onClick={() => toggle(sub.id)}
                      data-testid={`${testidPrefix}-sub-${sub.id}`}
                      className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs border transition-colors ${
                        active
                          ? 'bg-green-600 text-white border-green-600'
                          : 'bg-white text-gray-600 border-gray-200 hover:bg-green-50 hover:border-green-200'
                      }`}
                    >
                      {icon && (
                        <InterestIcon
                          icon={icon}
                          size={10}
                          className={active ? 'text-white' : 'text-green-600'}
                        />
                      )}
                      {sub.name}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default SubcategoryPicker;
